import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { CONFIRM_OPTIONS } from '../mock';

const SectionTitle = ({ children }) => (
  <h3 className="section-label uppercase mt-7 mb-3">{children}</h3>
);

const MemoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchMemory, confirmMemory, dismissMemory, pet } = useApp();
  const [memory, setMemory] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMemory(id)
      .then((data) => {
        if (alive) setMemory(data);
      })
      .catch(() => {
        if (alive) setNotFound(true);
      });
    return () => {
      alive = false;
    };
  }, [id, fetchMemory]);

  if (notFound) {
    return (
      <div className="px-6 pt-10" style={{ color: 'var(--lp-cream)' }}>
        Memory not found.
      </div>
    );
  }

  if (!memory) {
    return (
      <div className="px-6 pt-10" style={{ color: 'var(--lp-body)' }}>
        Loading…
      </div>
    );
  }

  const isConfirmed = memory.status === 'confirmed';

  const handleConfirm = async () => {
    const choice = CONFIRM_OPTIONS.find((o) => o.id === selected);
    setSubmitting(true);
    const updated = await confirmMemory(memory.id, choice ? choice.label : 'Walk');
    setMemory(updated);
    setSubmitting(false);
  };

  const handleDismiss = async () => {
    await dismissMemory(memory.id);
    navigate('/');
  };

  return (
    <div className="pb-10 lp-enter">
      {/* Header */}
      <div className="relative flex items-center justify-center px-6 pt-2 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-6 w-9 h-9 rounded-full flex items-center justify-center lp-pressable"
          style={{ border: '1px solid rgba(255,255,255,0.35)' }}
          aria-label="Back"
        >
          <ArrowLeft size={18} style={{ color: 'var(--lp-cream)' }} />
        </button>
        <h1 className="text-[18px] font-semibold" style={{ color: 'var(--lp-cream)' }}>
          Memory Detail
        </h1>
      </div>

      {/* Hero */}
      <div className="px-6">
        <img
          src={memory.heroImage}
          alt="Milo"
          className="w-full h-[220px] object-cover rounded-2xl"
        />
      </div>

      <div className="px-6">
        <h2
          className="font-serif-lp text-[30px] mt-5 mb-1.5"
          style={{ color: 'var(--lp-cream)' }}
        >
          {isConfirmed ? memory.confirmedTitle : memory.provisionalTitle}
        </h2>
        <p className="text-[14.5px] mb-5" style={{ color: 'var(--lp-body)' }}>
          {memory.startTime}–{memory.endTime} · {memory.duration}
          {!isConfirmed && ' · Needs confirmation'}
        </p>

        {/* Status card */}
        <div className="lp-card-dark p-4 flex items-center justify-between">
          <div>
            <p
              className="text-[13px] mb-0.5"
              style={{ color: 'var(--lp-muted)' }}
            >
              {isConfirmed ? 'Memory Status' : 'Review State'}
            </p>
            <p className="text-[15px]" style={{ color: 'var(--lp-cream)' }}>
              {isConfirmed ? 'Confirmed by user' : 'Waiting for your confirmation'}
            </p>
          </div>
          <span className="lp-badge">
            {isConfirmed ? 'Confirmed' : 'Needs confirmation'}
          </span>
        </div>

        {/* Confirmed-only: Details */}
        {isConfirmed && (
          <>
            <h3
              className="font-serif-lp text-[22px] mt-7 mb-3"
              style={{ color: 'var(--lp-cream)' }}
            >
              Details
            </h3>
            <div className="lp-card-dark p-4">
              {[
                ['Memory Type', memory.classification || 'Walk'],
                ['Start Time', memory.startTime],
                ['End Time', memory.endTime],
                ['Duration', memory.duration],
                ['Pet', pet ? pet.name : 'Milo'],
              ].map(([k, v], i, arr) => (
                <div
                  key={k}
                  className="flex items-center justify-between py-2.5"
                  style={{
                    borderBottom:
                      i < arr.length - 1
                        ? '1px solid rgba(255,255,255,0.07)'
                        : 'none',
                  }}
                >
                  <span className="text-[14.5px]" style={{ color: 'var(--lp-body)' }}>
                    {k}
                  </span>
                  <span
                    className="text-[14.5px] font-semibold"
                    style={{ color: 'var(--lp-cream)' }}
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* What Ring observed */}
        <SectionTitle>What Ring Observed</SectionTitle>
        <div className="lp-card-dark p-4">
          {isConfirmed && (
            <p
              className="text-[13.5px] mb-2 pb-2"
              style={{
                color: 'var(--lp-muted)',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              2 supported motion observations
            </p>
          )}
          {memory.observations.map((o, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5">
              <span
                className="text-[14px] w-[64px] flex-shrink-0"
                style={{ color: 'var(--lp-cream)' }}
              >
                {o.time}
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: 'var(--lp-body)' }}
              />
              <span className="text-[14px]" style={{ color: 'var(--lp-body)' }}>
                {o.text} · Source: {o.source}
              </span>
            </div>
          ))}
        </div>

        {/* What Lashachi inferred */}
        <SectionTitle>What Lashachi Inferred</SectionTitle>
        <div className="lp-card-dark p-4">
          <p
            className="text-[14px] leading-relaxed"
            style={{ color: 'var(--lp-body)' }}
          >
            {memory.inference}
          </p>
        </div>

        {/* User confirmation */}
        <SectionTitle>User Confirmation</SectionTitle>

        {!isConfirmed ? (
          <>
            <p className="text-[14px] mb-3" style={{ color: 'var(--lp-body)' }}>
              Classify this provisional event
            </p>
            <div className="lp-card-dark p-3 space-y-2.5">
              {CONFIRM_OPTIONS.map((opt) => {
                const isSel = selected === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setSelected(opt.id)}
                    className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl lp-pressable"
                    style={{
                      background: isSel
                        ? 'rgba(207,224,207,0.16)'
                        : 'rgba(255,255,255,0.05)',
                      border: isSel
                        ? '1px solid var(--lp-sage)'
                        : '1px solid rgba(255,255,255,0.10)',
                    }}
                  >
                    <span
                      className="text-[15px] font-medium"
                      style={{ color: 'var(--lp-cream)' }}
                    >
                      {opt.label}
                    </span>
                    <span
                      className="w-[18px] h-[18px] rounded-full flex items-center justify-center"
                      style={{
                        border: isSel
                          ? '5px solid var(--lp-sage)'
                          : '2px solid rgba(255,255,255,0.35)',
                      }}
                    />
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleConfirm}
              disabled={submitting}
              className="lp-btn-sage w-full py-4 text-[16px] mt-6"
              style={{ opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? 'Confirming…' : 'Confirm memory'}
            </button>
            <button
              onClick={handleDismiss}
              className="w-full text-center py-4 text-[15px] font-semibold lp-pressable"
              style={{ color: 'var(--lp-cream)' }}
            >
              Not related to {pet ? pet.name : 'Milo'}
            </button>
          </>
        ) : (
          <div className="lp-card-dark p-4">
            <p
              className="text-[14.5px] mb-3 pb-3"
              style={{
                color: 'var(--lp-cream)',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              {memory.classification || 'Walk'} · Confirmed by user
            </p>
            <div className="flex items-center justify-between py-1.5">
              <span className="text-[14.5px]" style={{ color: 'var(--lp-body)' }}>
                Activity
              </span>
              <span
                className="text-[14.5px] font-semibold"
                style={{ color: 'var(--lp-cream)' }}
              >
                {memory.classification || 'Walk'}
              </span>
            </div>
            <div className="flex items-center justify-between py-1.5">
              <span className="text-[14.5px]" style={{ color: 'var(--lp-body)' }}>
                Confirmed By
              </span>
              <span
                className="text-[14.5px] font-semibold"
                style={{ color: 'var(--lp-cream)' }}
              >
                {memory.confirmedBy || 'User'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemoryDetail;
